/**
 * Date utility functions for consistent date formatting across the application
 */

export class DateUtils {
  /**
   * Format a date for display in messages (dd MMM yyyy format)
   */
  static formatForMessage(date: any): string {
    try {
      // Handle Firestore timestamp objects
      if (date && typeof date === "object" && date._seconds) {
        return new Date(date._seconds * 1000).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      // Handle regular Date objects or date strings
      if (date instanceof Date) {
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      // Try to convert to Date
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return "Unknown date";
      }

      return dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      console.warn("Error formatting date:", error, date);
      return "Unknown date";
    }
  }

  /**
   * Format a date for display in UI components (dd MMM yyyy format)
   */
  static formatForDisplay(date: any, timezone?: string): string {
    try {
      let dateObj: Date;

      // Handle Firestore timestamp objects
      if (date && typeof date === "object" && date._seconds) {
        dateObj = new Date(date._seconds * 1000);
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        // Try to convert to Date
        dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          return "";
        }
      }

      // Format with timezone if provided
      const options: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
      };

      if (timezone) {
        options.timeZone = timezone;
      }

      return dateObj.toLocaleDateString("en-GB", options);
    } catch (error) {
      console.warn("Error formatting date for display:", error, date);
      return "";
    }
  }

  /**
   * Format a date for time display (e.g., "2:30 PM")
   */
  static formatTime(date: any, timezone?: string): string {
    try {
      let dateObj: Date;

      // Handle Firestore timestamp objects
      if (date && typeof date === "object" && date._seconds) {
        dateObj = new Date(date._seconds * 1000);
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        // Try to convert to Date
        dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          return "";
        }
      }

      // Format with timezone if provided
      const options: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      };

      if (timezone) {
        options.timeZone = timezone;
      }

      return dateObj.toLocaleTimeString("en-US", options);
    } catch (error) {
      console.warn("Error formatting time:", error, date);
      return "";
    }
  }

  /**
   * Check if a date is valid
   */
  static isValidDate(date: any): boolean {
    try {
      if (!date) return false;

      // Handle Firestore timestamp objects
      if (date && typeof date === "object" && date._seconds) {
        const dateObj = new Date(date._seconds * 1000);
        return !isNaN(dateObj.getTime());
      }

      // Handle regular Date objects or date strings
      if (date instanceof Date) {
        return !isNaN(date.getTime());
      }

      // Try to convert to Date
      const dateObj = new Date(date);
      return !isNaN(dateObj.getTime());
    } catch (error) {
      return false;
    }
  }

  /**
   * Convert any date format to a standard Date object
   */
  static toDate(date: any): Date | null {
    try {
      if (!date) return null;

      // Handle Firestore timestamp objects
      if (date && typeof date === "object" && date._seconds) {
        return new Date(date._seconds * 1000);
      }

      // Handle regular Date objects
      if (date instanceof Date) {
        return date;
      }

      // Try to convert to Date
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return null;
      }

      return dateObj;
    } catch (error) {
      console.warn("Error converting to date:", error, date);
      return null;
    }
  }

  /**
   * Check if a date is today
   */
  static isToday(date: any, timezone?: string): boolean {
    const dateObj = this.toDate(date);
    if (!dateObj) return false;

    const today = new Date();

    if (timezone) {
      // Compare dates in the specified timezone
      const dateInTz = new Date(dateObj.toLocaleString("en-US", { timeZone: timezone }));
      const todayInTz = new Date(today.toLocaleString("en-US", { timeZone: timezone }));
      return dateInTz.toDateString() === todayInTz.toDateString();
    }

    return dateObj.toDateString() === today.toDateString();
  }

  /**
   * Check if a date is tomorrow
   */
  static isTomorrow(date: any, timezone?: string): boolean {
    const dateObj = this.toDate(date);
    if (!dateObj) return false;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (timezone) {
      // Compare dates in the specified timezone
      const dateInTz = new Date(dateObj.toLocaleString("en-US", { timeZone: timezone }));
      const tomorrowInTz = new Date(tomorrow.toLocaleString("en-US", { timeZone: timezone }));
      return dateInTz.toDateString() === tomorrowInTz.toDateString();
    }

    return dateObj.toDateString() === tomorrow.toDateString();
  }

  /**
   * Check if a date is yesterday
   */
  static isYesterday(date: any, timezone?: string): boolean {
    const dateObj = this.toDate(date);
    if (!dateObj) return false;

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (timezone) {
      // Compare dates in the specified timezone
      const dateInTz = new Date(dateObj.toLocaleString("en-US", { timeZone: timezone }));
      const yesterdayInTz = new Date(yesterday.toLocaleString("en-US", { timeZone: timezone }));
      return dateInTz.toDateString() === yesterdayInTz.toDateString();
    }

    return dateObj.toDateString() === yesterday.toDateString();
  }

  /**
   * Format a date with relative terms (Today, Tomorrow, Yesterday)
   */
  static formatRelative(date: any, timezone?: string): string {
    if (this.isToday(date, timezone)) return "Today";
    if (this.isTomorrow(date, timezone)) return "Tomorrow";
    if (this.isYesterday(date, timezone)) return "Yesterday";
    return this.formatForDisplay(date, timezone);
  }
}
